import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, CheckCircle2, Shield, Lock } from 'lucide-react';
import Button from '../../../../components/common/Button';
import Alert from '../../../../components/common/Alert';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

// Stripe Card Element styles
const cardStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '16px',
    },
    invalid: {
      color: '#e53e3e',
      iconColor: '#e53e3e',
    },
  },
};

const PaymentForm = ({ subscriptionData, onComplete, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Stripe has not been initialized');
      setProcessing(false);
      return;
    }

    try {
      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Return the payment method ID to the parent component
      onComplete({
        paymentMethodId: paymentMethod.id,
        completed: true
      });
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  // Calculate the total price based on subscription data
  const calculateTotal = () => {
    // This is a placeholder - you would need to get the actual plan price from your API
    const basePricePerSeat = subscriptionData.billingPeriod === 'yearly' ? 120 : 12; // $12/mo or $120/yr
    return basePricePerSeat * subscriptionData.seats;
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-5">
          <CreditCard className="h-10 w-10 text-primary" />
        </div>
        <h2 className="text-3xl font-bold text-navy mb-3">Complete Your Purchase</h2>
        <p className="text-navy/70 max-w-lg mx-auto text-lg">
          Your subscription will begin immediately after payment is processed
        </p>
      </div>

      {error && (
        <Alert type="error" className="max-w-3xl mx-auto">
          {error}
        </Alert>
      )}

      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left side - Payment form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8">
                  <h3 className="font-semibold text-navy text-xl mb-6">Payment Details</h3>
                  
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Card Information
                    </label>
                    <div className="border border-gray-300 rounded-lg p-5 focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary transition-all">
                      <CardElement options={cardStyle} />
                    </div>
                    <div className="flex items-center mt-3 text-sm text-gray-500">
                      <Lock className="h-4 w-4 mr-2" />
                      <span>Your payment information is secured with 256-bit encryption</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={onBack} 
                      disabled={processing}
                      className="w-full sm:w-auto"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={processing || !stripe}
                      className="w-full sm:w-auto px-8"
                    >
                      {processing ? (
                        <>
                          <span className="animate-spin mr-2 inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                          Processing...
                        </>
                      ) : (
                        'Complete Payment'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Right side - Order summary */}
          <div>
            <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6">
                <h3 className="font-semibold text-navy text-lg mb-5">Order Summary</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-navy">{subscriptionData.planName}</p>
                      <p className="text-sm text-gray-600 capitalize">{subscriptionData.billingPeriod} billing</p>
                    </div>
                    <span className="text-navy font-medium">
                      ${(calculateTotal() / subscriptionData.seats).toFixed(2)}/{subscriptionData.billingPeriod === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-navy">Seats</p>
                      <p className="text-sm text-gray-600">{subscriptionData.seats} {subscriptionData.seats === 1 ? 'user' : 'users'}</p>
                    </div>
                    <span className="text-navy font-medium">
                      x{subscriptionData.seats}
                    </span>
                  </div>
                  
                  {subscriptionData.billingPeriod === 'yearly' && (
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200 text-green-600">
                      <p className="font-medium">Annual discount</p>
                      <span className="font-medium">-20%</span>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between items-center text-lg font-bold text-navy mb-6">
                  <span>Total</span>
                  <span>${calculateTotal().toFixed(2)}/{subscriptionData.billingPeriod === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>Cancel anytime</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>Automatic renewal</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Shield className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>Secure checkout</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PaymentProcessor(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
} 